import { v7 as uuidv7 } from 'uuid';

import type { ConversationItem } from '@/components/ai-image-assistant/types';
import { readImageGenerationConfirmation } from '@/lib/assistant/image-generation-confirmation';
import { getChunkPayload, getStreamErrorMessage } from '@/lib/mastra/stream';
import type { ChunkType } from '@/lib/mastra/types';
import { readString } from '@/lib/prop-utils';

const GENERATE_IMAGE_TOOL_NAME = 'generateImage';
const APPLY_IMAGE_TRANSFORM_TOOL_NAME = 'applyImageTransform';
const OPEN_IMAGE_EDITOR_TOOL_NAME = 'openImageEditor';

function parseToolOutput(raw: unknown): Record<string, unknown> {
  try {
    if (typeof raw === 'string') return JSON.parse(raw) as Record<string, unknown>;
    if (raw && typeof raw === 'object') return raw as Record<string, unknown>;
  } catch {
    // fall through
  }
  return {};
}

export interface ImageCreatorChunkContext {
  prompt: string;
  rootPrompt: string;
}

export function processImageCreatorStreamChunk(
  items: ConversationItem[],
  chunk: ChunkType,
  ctx: ImageCreatorChunkContext,
): ConversationItem[] {
  const payload = getChunkPayload(chunk);
  const toolName = readString(payload.toolName);

  switch (chunk.type) {
    case 'tool-call': {
      if (
        toolName !== GENERATE_IMAGE_TOOL_NAME &&
        toolName !== APPLY_IMAGE_TRANSFORM_TOOL_NAME &&
        toolName !== OPEN_IMAGE_EDITOR_TOOL_NAME
      ) {
        return items;
      }
      for (let i = items.length - 1; i >= 0; i--) {
        if (items[i].type === 'thinking') {
          const updated = [...items];
          if (toolName === OPEN_IMAGE_EDITOR_TOOL_NAME) {
            updated[i] = { id: items[i].id, type: 'editor-open', text: 'Opening image editor…' };
          } else {
            updated[i] = {
              id: items[i].id,
              type: 'generating',
              prompt: ctx.prompt,
            };
          }
          return updated;
        }
      }
      return items;
    }

    case 'text-delta': {
      const delta = readString(payload.text) ?? '';
      if (!delta) return items;

      for (let i = items.length - 1; i >= 0; i--) {
        const item = items[i];
        if (item.type === 'user') break;
        // The image comes from the tool result; text after it is a note for the user.
        if (item.type === 'image') {
          return [...items, { id: uuidv7(), type: 'assistant', text: delta }];
        }
        if (item.type === 'error') return items;
        if (item.type === 'generating' || item.type === 'editor-open') return items;
        if (item.type === 'thinking') {
          const updated = [...items];
          updated[i] = { id: item.id, type: 'assistant', text: delta };
          return updated;
        }
        if (item.type === 'assistant') {
          if (item.imageConfirmation || item.imagePolicyNote) return items;
          const updated = [...items];
          updated[i] = { ...item, text: item.text + delta };
          return updated;
        }
      }
      return items;
    }

    case 'tool-result': {
      if (toolName !== GENERATE_IMAGE_TOOL_NAME) return items;

      let generatingIndex = -1;
      for (let i = items.length - 1; i >= 0; i--) {
        if (items[i].type === 'generating') {
          generatingIndex = i;
          break;
        }
      }
      if (generatingIndex === -1) return items;

      const output = parseToolOutput(payload.result);
      const updated = [...items];

      const confirmation = readImageGenerationConfirmation(output);
      if (confirmation) {
        updated[generatingIndex] = {
          id: items[generatingIndex].id,
          type: 'assistant',
          text: confirmation.message,
          imageConfirmation: confirmation,
        };
        return updated;
      }

      if (typeof output.imageUrl === 'string') {
        updated[generatingIndex] = {
          id: items[generatingIndex].id,
          type: 'image',
          imageUrl: output.imageUrl,
          prompt: ctx.prompt,
          rootPrompt: ctx.rootPrompt,
        };
        if (typeof output.note === 'string') {
          updated.push({
            id: uuidv7(),
            type: 'assistant',
            text: output.note,
            imagePolicyNote: true,
          });
        }
      } else {
        const errorMessage =
          typeof output.message === 'string'
            ? output.message
            : 'Image generation failed. Please try again.';
        updated[generatingIndex] = {
          id: items[generatingIndex].id,
          type: 'error',
          text: errorMessage,
          prompt: ctx.prompt,
        };
      }
      return updated;
    }

    case 'error':
      throw new Error(getStreamErrorMessage(payload.error));

    default:
      return items;
  }
}
